import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from "react";

import { t } from "i18next";

import DensityLargeIcon from "@mui/icons-material/DensityLarge";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";

import { primaryAccentColor } from "@/src/theme/helpers";

import { toolbarSx } from "@/src/components/organisms/AdminDataTable/adminDataTable.styles";
import { DensityMode } from "@/src/components/organisms/AdminDataTable/adminDataTable.types";
import { Table } from "@/src/components/organisms/AdminDataTable/reactTable";
import { useCsvExport } from "@/src/components/organisms/AdminDataTable/useCsvExport";

const SEARCH_DEBOUNCE_MS = 300;

interface AdminDataTableToolbarProps<TData extends object> {
  table: Table<TData>;
  density: DensityMode;
  onDensityChange: (density: DensityMode) => void;
  exportFileName: string;
  title?: string;
  globalFilter: string;
  filterResetVersion: number;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  enableCsvExport: boolean;
  isLoading?: boolean;
  searchExtras?: ReactNode;
  extras?: ReactNode;
  scrollAtEnd?: boolean;
  onScrollToggle?: () => void;
}

const AdminDataTableToolbar = <TData extends object>(
  props: AdminDataTableToolbarProps<TData>
) => {
  const {
    table,
    density,
    onDensityChange,
    exportFileName,
    title,
    globalFilter,
    filterResetVersion,
    hasActiveFilters,
    onResetFilters,
    enableCsvExport,
    isLoading = false,
    searchExtras,
    extras,
    scrollAtEnd,
    onScrollToggle
  } = props;
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const exportCsv = useCsvExport(table, exportFileName);

  const [searchInput, setSearchInput] = useState(globalFilter);
  const lastGlobalFilterRef = useRef(globalFilter);
  const lastFilterResetVersionRef = useRef(filterResetVersion);

  // Syncs an externally-driven globalFilter change (e.g. clear filters) into
  // the input. Changing searchInput here also cancels any pending debounce
  // below, so a stale keystroke can't overwrite the external change.
  //
  // filterResetVersion is checked alongside globalFilter's value because a
  // reset can set globalFilter to a value it already had - e.g. clearing
  // filters while the debounce below hasn't committed a pending keystroke
  // yet, so globalFilter is still "". Relying on value equality alone would
  // miss that reset, leaving the pending debounce free to fire afterwards
  // and silently reinstate the search term the reset was meant to clear.
  useEffect(() => {
    if (
      globalFilter === lastGlobalFilterRef.current &&
      filterResetVersion === lastFilterResetVersionRef.current
    ) {
      return;
    }
    lastGlobalFilterRef.current = globalFilter;
    lastFilterResetVersionRef.current = filterResetVersion;
    setSearchInput(globalFilter);
  }, [globalFilter, filterResetVersion]);

  useEffect(() => {
    if (searchInput === globalFilter) return;
    const id = setTimeout(() => {
      lastGlobalFilterRef.current = searchInput;
      table.setGlobalFilter(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput, globalFilter, table]);

  const handleDensityChange = (
    _event: MouseEvent<HTMLElement>,
    value: DensityMode | null
  ) => {
    if (value) onDensityChange(value);
  };

  const toggleableColumns = table
    .getAllLeafColumns()
    .filter((col) => col.getCanHide());

  return (
    <Box
      sx={toolbarSx}
      role="toolbar"
      aria-label={t("admin.dataTable.toolbar.ariaLabel")}
    >
      {title && (
        <>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            {title}
          </Typography>
          <Divider orientation="vertical" flexItem />
        </>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap"
        }}
      >
        {searchExtras}
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("admin.dataTable.toolbar.searchPlaceholder")}
          aria-label={t("admin.dataTable.toolbar.quickSearchAriaLabel")}
          disabled={isLoading}
          sx={{ minWidth: 220 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }
          }}
        />
      </Box>
      <Tooltip title={t("admin.dataTable.toolbar.clearFilters")}>
        <span>
          <Button
            size="small"
            variant={hasActiveFilters ? "contained" : "text"}
            color={hasActiveFilters ? "primary" : "secondary"}
            startIcon={<FilterAltOffIcon />}
            onClick={onResetFilters}
            disabled={isLoading || !hasActiveFilters}
            aria-label={t("admin.dataTable.toolbar.clearAllFiltersAriaLabel")}
          >
            {t("admin.dataTable.toolbar.clearFilters")}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={t("admin.dataTable.toolbar.columns")}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(columnsAnchor)}
            disabled={isLoading}
          >
            {t("admin.dataTable.toolbar.columns")}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 400 } } }}
      >
        <MenuItem>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={table.getIsAllColumnsVisible()}
                indeterminate={
                  !table.getIsAllColumnsVisible() &&
                  table.getAllLeafColumns().some((c) => c.getIsVisible())
                }
                onChange={(e) => {
                  e.stopPropagation();
                  table.toggleAllColumnsVisible(
                    !table.getIsAllColumnsVisible()
                  );
                }}
              />
            }
            label={t("admin.dataTable.toolbar.allColumns")}
            sx={{ m: 0 }}
          />
        </MenuItem>
        <Divider />
        {toggleableColumns.map((column) => {
          const meta = column.columnDef.meta as
            | { exportHeader?: string }
            | undefined;
          const label =
            typeof column.columnDef.header === "string"
              ? column.columnDef.header
              : (meta?.exportHeader ?? column.id);
          return (
            <MenuItem key={column.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={column.getIsVisible()}
                    onChange={(e) => {
                      e.stopPropagation();
                      column.toggleVisibility();
                    }}
                  />
                }
                label={label}
                sx={{ m: 0 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
      {extras && (
        <>
          <Divider orientation="vertical" flexItem />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap"
            }}
          >
            {extras}
          </Box>
        </>
      )}
      {onScrollToggle && (
        <Tooltip
          title={
            scrollAtEnd
              ? t("admin.dataTable.toolbar.scrollBackLeft")
              : t("admin.dataTable.toolbar.scrollRight")
          }
          placement="top"
        >
          <span>
            <IconButton
              size="small"
              onClick={onScrollToggle}
              disabled={isLoading}
              aria-label={
                scrollAtEnd
                  ? t("admin.dataTable.toolbar.scrollTableLeftAriaLabel")
                  : t("admin.dataTable.toolbar.scrollTableRightAriaLabel")
              }
            >
              {scrollAtEnd ? (
                <KeyboardArrowLeftIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <ToggleButtonGroup
        size="small"
        value={density}
        exclusive
        onChange={handleDensityChange}
        aria-label={t("admin.dataTable.toolbar.rowHeightAriaLabel")}
        disabled={isLoading}
      >
        <ToggleButton
          value="compact"
          aria-label={t("admin.dataTable.toolbar.densityCompact")}
        >
          <Tooltip title={t("admin.dataTable.toolbar.densityCompact")}>
            <DensitySmallIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton
          value="standard"
          aria-label={t("admin.dataTable.toolbar.densityStandard")}
        >
          <Tooltip title={t("admin.dataTable.toolbar.densityStandard")}>
            <DensityMediumIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton
          value="comfortable"
          aria-label={t("admin.dataTable.toolbar.densityComfortable")}
        >
          <Tooltip title={t("admin.dataTable.toolbar.densityComfortable")}>
            <DensityLargeIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ flex: 1 }} />
      {enableCsvExport && (
        <Tooltip title={t("admin.dataTable.toolbar.exportCsvTooltip")}>
          <span>
            <IconButton
              size="small"
              sx={(muiTheme) => ({ color: primaryAccentColor(muiTheme) })}
              onClick={exportCsv}
              aria-label={t("admin.dataTable.toolbar.exportCsvAriaLabel")}
              disabled={isLoading}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default AdminDataTableToolbar;
